import { Clear } from "@mui/icons-material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, Button, Drawer, Stack, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MEASURE_TYPES } from "../../utils/constants";
import { get, post } from "../../utils/io";
import ContractIcon from "../icons/ContractIcon";
import StelDataGrid from "../StelDataGrid";
import LoadingHub from "./LoadingHub";

const POLLING_INTERVAL = 5000;

function TrustedDevice(props) {
  const { hubId } = useParams();
  const timeoutRef = useRef(null);

  const { isOpen, handleTrustedDevice, isHubConnected } = props;
  const [listTrustedDevices, setListTrustedDevices] = useState([]);
  const [discoveryInfo, setDiscoveryInfo] = useState({});
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [selectedDevice, setSelectedDevice] = useState([]);
  const [loadingDiscoveryDevice, setLoadingDiscoveryDevice] = useState(false);

  const columns = [
    { field: "mac_address", headerName: "MAC Address", flex: 1 },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      renderCell: (params) =>
        MEASURE_TYPES[params?.value]?.label || params.row.type,
    },
    {
      field: "is_blocked",
      headerName: "Status",
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "center",
            height: "100%",
          }}
        >
          {params.row.is_blocked ? (
            <ClearIcon color="error" />
          ) : (
            <CheckIcon color="success" />
          )}
        </Box>
      ),
      flex: 0.5,
    },
    {
      field: "payload_schema",
      headerName: "Last Measurement",
      renderCell: (params) => JSON.stringify(params.row?.last_measure),
      flex: 1,
    },
  ];

  const getDevicesFromRow = (ids) =>
    (listTrustedDevices || [])
      .filter((device) => ids.includes(device.id))
      .map((device) => device.mac_address);

  const handleSelectRow = (ids) => {
    const row = getDevicesFromRow(ids);
    setSelectedDevice(row || []);
  };

  const fetchTrustedDevices = async () => {
    const res = await get("/trusted_devices", {
      hub_id: hubId.substring(0, 10),
      offset: paginationModel.page,
      limit: paginationModel.pageSize,
    });
    setDiscoveryInfo(res?.data?.discovery_info || {});
    return res;
  };

  const getListTrustedDevices = async () => {
    setLoading(true);
    const res = await fetchTrustedDevices();
    setTotalCount(res.data?.total_count || 0);
    setLoading(false);
    setLoadingDiscoveryDevice(res.data?.discovery_info?.is_discovering);
    if (res.data?.discovery_info?.is_discovering && isOpen) {
      timeoutRef.current = setTimeout(getListTrustedDevices, POLLING_INTERVAL);
    }

    setListTrustedDevices(res.data.devices);
  };

  const lastAutoDiscovery = new Date(discoveryInfo?.last_auto_discovery);
  const lastManualDiscovery = new Date(discoveryInfo?.last_manual_discovery);

  const showDiscoveryInfo =
    discoveryInfo?.last_auto_discovery || discoveryInfo?.last_manual_discovery;

  const triggerType =
    lastAutoDiscovery > lastManualDiscovery ? "auto" : "manual";

  const lastTrigger = (
    (triggerType === "auto" ? lastAutoDiscovery : lastManualDiscovery) || ""
  ).toLocaleString();

  const handleDiscoveryDevice = async () => {
    setLoadingDiscoveryDevice(true);
    await get(`hubs/${hubId}/discover_new_device`, { timeout: -1 });
    const fetchDevices = async () => {
      const res = await fetchTrustedDevices();
      return res.data;
    };
    const checkDevices = async () => {
      const dataDevice = await fetchDevices();
      if (dataDevice?.total_count && dataDevice.total_count - totalCount > 0) {
        enqueueSnackbar(
          `We have found ${dataDevice.total_count - totalCount} devices.`,
          {
            variant: "success",
          }
        );
        await getListTrustedDevices();

        return;
      }
      setLoadingDiscoveryDevice(dataDevice?.discovery_info?.is_discovering);
      timeoutRef.current = setTimeout(checkDevices, POLLING_INTERVAL);
    };

    checkDevices();
  };

  const handleBlockDevices = async () => {
    const res = await post("/trusted_devices", {
      hub_id: hubId.substring(0, 10),
      mac_addresses: selectedDevice,
    });
    if (res.status === 204) {
      enqueueSnackbar(`Successfully blocked device(s).`, {
        variant: "success",
      });
      getListTrustedDevices();
      return;
    }
    enqueueSnackbar(`Failed to block device(s).`, {
      variant: "error",
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    getListTrustedDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hubId, isOpen, paginationModel]);

  useEffect(() => {
    if (!isOpen && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [isOpen]);

  return (
    <Stack>
      <Drawer anchor="right" open={isOpen} onClose={handleTrustedDevice}>
        <Stack width={700}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 16px",
              borderBottom: "1px solid #0000003B",
            }}
          >
            <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
              Trusted Devices
            </Typography>
            <Button onClick={handleTrustedDevice} sx={{ minWidth: 0 }}>
              <Clear sx={{ color: "#000" }} />
            </Button>
          </Box>
          <Stack sx={{ padding: "24px 16px 12px 16px", gap: "16px" }}>
            <Stack
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                TRUSTED DEVICE LIST
              </Typography>
              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  disabled={!selectedDevice?.length || loading}
                  variant="outlined"
                  onClick={handleBlockDevices}
                >
                  Block Device
                </Button>
                <Button
                  disabled={loadingDiscoveryDevice || isHubConnected}
                  variant="contained"
                  onClick={handleDiscoveryDevice}
                >
                  Discover Devices
                </Button>
              </Box>
            </Stack>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                justifyContent: "end",
              }}
            >
              {showDiscoveryInfo ? (
                <>
                  <Typography
                    sx={{ fontSize: 12, fontWeight: 600, color: "#636D76" }}
                  >
                    {triggerType === "auto"
                      ? "Automatically triggered at"
                      : "Manually triggered at"}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#636D76" }}>
                    {lastTrigger || "-"}
                  </Typography>
                </>
              ) : (
                <Typography
                  sx={{ fontSize: 12, fontWeight: 600, color: "#636D76" }}
                >
                  No discovery attempt has been made yet
                </Typography>
              )}
            </Box>
            <Stack
              sx={{
                width: "100%",
                justifyContent: "center",
              }}
            >
              {loadingDiscoveryDevice && (
                <Stack sx={{ justifyContent: "center", alignItems: "center" }}>
                  <LoadingHub />
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 500, color: "#636D76" }}
                  >
                    Discovering the devices.
                  </Typography>
                </Stack>
              )}
              {listTrustedDevices && !listTrustedDevices?.length && !loading ? (
                <Stack sx={{ justifyContent: "center", alignItems: "center" }}>
                  <ContractIcon />
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 500, color: "#636D76" }}
                  >
                    No devices.
                  </Typography>
                </Stack>
              ) : (
                <StelDataGrid
                  rows={listTrustedDevices || []}
                  columns={columns}
                  loading={loading}
                  checkboxSelection
                  paginationModel={paginationModel}
                  pageSizeOptions={[10, 25, 50, 100]}
                  rowCount={totalCount}
                  onRowSelectionModelChange={(ids) => handleSelectRow(ids)}
                  onPaginationModelChange={setPaginationModel}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Drawer>
    </Stack>
  );
}

export default TrustedDevice;

TrustedDevice.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleTrustedDevice: PropTypes.func.isRequired,
  isHubConnected: PropTypes.bool.isRequired,
};
